<template>
    <div class="public-landing-page">
        <!-- Loading State -->
        <div v-if="loading" class="d-flex flex-column justify-content-center align-items-center vh-100 bg-light">
            <div class="spinner-border" style="color: #5F0487;" role="status"></div>
            <span class="mt-3 fw-bold text-muted">Carregando página...</span>
        </div>

        <!-- Erro / 404 -->
        <div v-else-if="error" class="d-flex flex-column justify-content-center align-items-center vh-100 bg-light">
            <i class="bi bi-emoji-frown fs-1 text-secondary mb-3"></i>
            <h2 class="fw-bold text-dark">Página não encontrada</h2>
            <p class="text-muted">A página que você está procurando não existe ou foi desativada.</p>
        </div>

        <!-- Renderização da Página com Container Centralizado -->
        <div v-else class="page-wrapper">
            <div class="page-content modern-preview-font shadow-sm">
                <PagePreviewRenderer :form="form" @openCheckout="handleCheckout" />
            </div>
        </div>

        <!-- O MODAL DE DOAÇÃO -->
        <transition name="fade">
            <DonationModal v-if="isDonationModalOpen" :campaign="activeCampaign" @close="closeModal" />
        </transition>
    </div>
</template>

<script>
import api from '@/services/api';
import PagePreviewRenderer from '../components/page/PagePreviewRenderer.vue';
import DonationModal from '@/components/DonationModal.vue';

export default {
    name: 'LandingPageView',
    components: {
        PagePreviewRenderer,
        DonationModal
    },
    data() {
        return {
            loading: true,
            error: false,
            isDonationModalOpen: false,
            activeCampaign: null,
            pageId: null,
            form: {
                title: '',
                title_color: '',
                sections: []
            }
        };
    },
    async created() {
        await this.fetchPublicPage();
    },
    unmounted() {
        this.clearMetaTags();
    },
    methods: {
        async fetchPublicPage() {
            const slug = this.$route.params.slug;

            try {
                const response = await api.get(`/public/landing-pages/${slug}`);
                const pageData = response.data.data || response.data;
                this.pageId = pageData.id;

                const crowdfundingBlock = (pageData.content || []).find(s => s.type === 'dailypag_crowdfunding');
                const goal_amount = parseFloat(pageData.goal_amount || crowdfundingBlock?.content?.goal_amount || 0);
                const total_raised = parseFloat(pageData.total_raised || 0);

                const progress_percentage = goal_amount > 0
                    ? Math.round((total_raised / goal_amount) * 100)
                    : 0;

                const total_supporters = pageData.total_supporters || 0;
                const supporters = pageData.supporters || response.data.supporters || [];

                const sectionsWithMetrics = (pageData.content || []).map(section => {
                    return {
                        ...section,
                        total_raised,
                        goal_amount,
                        progress_percentage,
                        total_supporters,
                        supporters
                    };
                });

                this.form = {
                    id: pageData.id,
                    title: pageData.title,
                    title_color: pageData.title_color,
                    sections: sectionsWithMetrics,
                    supporters
                };

                document.title = this.form.title;
                this.updateOpenGraph(pageData);

            } catch (err) {
                console.error("Erro ao carregar página pública:", err);
                this.error = true;
            } finally {
                this.loading = false;
            }
        },

        updateOpenGraph(pageData) {
            const carouselBlock = (pageData.content || []).find(s => s.type === 'carousel');
            const ogImage = carouselBlock?.content?.images?.[0]?.url || 'https://sua-url-do-dailypag.com/logo-og.png';

            const textBlock = (pageData.content || []).find(s => s.type === 'text');
            let ogDescription = 'Apoie esta campanha através do Dailypag. Sua contribuição faz toda a diferença!';
            if (textBlock && textBlock.content && textBlock.content.text) {
                ogDescription = textBlock.content.text.replace(/<[^>]*>?/gm, '').substring(0, 140) + '...';
            }

            const metaTags = {
                'og:title': pageData.title,
                'og:description': ogDescription,
                'og:image': ogImage,
                'og:url': window.location.href,
                'og:type': 'website',
                'twitter:card': 'summary_large_image',
                'twitter:title': pageData.title,
                'twitter:description': ogDescription,
                'twitter:image': ogImage
            };

            Object.entries(metaTags).forEach(([key, value]) => {
                let tag = document.querySelector(`meta[property="${key}"]`) || document.querySelector(`meta[name="${key}"]`);
                if (!tag) {
                    tag = document.createElement('meta');
                    tag.setAttribute(key.startsWith('og:') ? 'property' : 'name', key);
                    // Adiciona classe para facilitar a remoção depois
                    tag.classList.add('dynamic-meta-tag');
                    document.head.appendChild(tag);
                }
                tag.setAttribute('content', value);
            });
        },

        clearMetaTags() {
            const dynamicTags = document.querySelectorAll('.dynamic-meta-tag');
            dynamicTags.forEach(tag => tag.remove());
        },

        handleCheckout(content) {
            this.activeCampaign = {
                id: this.pageId,
                title: content.title || this.form.title,
                amount: content.amount,
                allows_fractional: content.allows_fractional
            };
            this.isDonationModalOpen = true;
        },

        closeModal() {
            this.isDonationModalOpen = false;
            this.activeCampaign = null;
            this.fetchPublicPage();
        }
    }
}
</script>

<style scoped>
.public-landing-page {
    min-height: 100vh;
    width: 100%;
    background-color: #f8fafc;
    margin: 0;
    padding: 0;
    overflow-x: hidden;
}

.page-wrapper {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 2rem 1rem;
}

.page-content {
    background-color: #ffffff;
    border-radius: 16px;
    padding: 1.5rem;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

@media (max-width: 768px) {
    .page-wrapper {
        padding: 0;
    }
    .page-content {
        border-radius: 0;
        padding: 0.5rem;
    }
}
</style>